export const licensingRoles = ["licensing-admin", "licensing-operator", "licensing-release-approver"] as const;

export type LicensingRole = (typeof licensingRoles)[number];

export type AuthTokenSnapshot = {
  token: string;
  sessionId: number;
  generation: number;
  roles: ReadonlySet<LicensingRole>;
};

export type TokenSessionSource = {
  updateToken: (minimumValiditySeconds: number) => Promise<boolean>;
  readToken: () => string | undefined;
  readRoles: () => ReadonlySet<LicensingRole>;
};

export class TokenSession {
  private source: TokenSessionSource | null = null;
  private snapshot: AuthTokenSnapshot | null = null;
  private sessionSequence = 0;
  private renewalQueue: Promise<void> = Promise.resolve();

  establish(source: TokenSessionSource) {
    const token = source.readToken();
    if (!token) {
      throw new Error("token unavailable");
    }
    this.sessionSequence += 1;
    this.source = source;
    this.snapshot = {
      token,
      sessionId: this.sessionSequence,
      generation: 0,
      roles: copyRoles(source.readRoles())
    };
    return this.snapshot;
  }

  replace(source: TokenSessionSource) {
    return this.establish(source);
  }

  clear() {
    this.sessionSequence += 1;
    this.source = null;
    this.snapshot = null;
  }

  current() {
    return this.snapshot;
  }

  async get(forceRefresh = false): Promise<AuthTokenSnapshot> {
    const source = this.source;
    const starting = this.snapshot;
    if (!source || !starting) {
      throw new Error("not authenticated");
    }

    let result: AuthTokenSnapshot | null = null;
    const operation = this.renewalQueue.then(async () => {
      const before = this.snapshot;
      if (!before || this.source !== source || before.sessionId !== starting.sessionId) {
        throw new Error("authentication session changed");
      }
	  if (before.generation > starting.generation) {
		result = before;
		return;
	  }
      const renewed = await source.updateToken(forceRefresh ? -1 : 30);
      const current = this.snapshot;
      if (!current || this.source !== source || current.sessionId !== starting.sessionId) {
        throw new Error("authentication session changed");
      }
      if (renewed) {
        const token = source.readToken();
        if (!token) {
          throw new Error("token unavailable");
        }
        this.snapshot = {
          token,
          sessionId: current.sessionId,
          generation: current.generation + 1,
          roles: copyRoles(source.readRoles())
        };
      }
      result = this.snapshot;
    });
    this.renewalQueue = operation.catch(() => undefined);
    await operation;
    if (!result) {
      throw new Error("token unavailable");
    }
    return result;
  }
}

function copyRoles(roles: ReadonlySet<LicensingRole>) {
  return new Set(roles);
}
